import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { MapPin, Hammer, Check } from 'lucide-react'
import { Button } from 'components/ui/button'
import { Spinner } from 'components/ui/spinner'
import { cn } from 'lib/utils'
import { fetchPortfolioPartnerScope } from 'slices/portfolioSlice'

type AccessChoice = 'full' | 'readonly' | 'none'

interface ScopeProject {
	_id: string
	projectName: string
	projectType?: string
	alreadyPartner: boolean
	currentAccessLevel: string | null
}

interface ScopeLocation {
	_id: string
	assetName: string
	city?: string
	stateProvince?: string
	alreadyPartner: boolean
	currentAccessLevel: string | null
	projects: ScopeProject[]
}

interface PortfolioPartnerAccessChooserProps {
	portfolioId: string
	/** The MyPartner record chosen in the previous step. */
	partner: any
	onCancel: () => void
	onConfirm: (payload: {
		portfolioAccessLevel: 'full' | 'readonly'
		grants: Array<{ assetType: 'location' | 'project'; assetId: string; accessLevel: AccessChoice }>
	}) => void
}

const CHOICES: Array<{ value: AccessChoice; label: string }> = [
	{ value: 'full', label: 'Full' },
	{ value: 'readonly', label: 'Read Only' },
	{ value: 'none', label: 'Skip' },
]

/**
 * Step 2 of adding a portfolio partner: choose an access level for each
 * location and project in the portfolio.
 *
 * Assets the person already partners on are rendered inert and are excluded
 * from the emitted `grants`, so an existing partnership is never altered or
 * downgraded. The backend enforces the same rule atomically; this is the
 * visible half of it.
 *
 * There is no radio-group primitive in components/ui, so the three-way control
 * is a segmented button group rather than a new Radix dependency.
 */
const PortfolioPartnerAccessChooser = ({
	portfolioId,
	partner,
	onCancel,
	onConfirm,
}: PortfolioPartnerAccessChooserProps) => {
	const dispatch = useDispatch<any>()

	const [locations, setLocations] = useState<ScopeLocation[]>([])
	const [isLoading, setIsLoading] = useState(true)
	const [loadError, setLoadError] = useState<string | null>(null)
	const [choices, setChoices] = useState<Record<string, AccessChoice>>({})
	const [futureAccess, setFutureAccess] = useState<'full' | 'readonly'>('readonly')

	const partnerUserId = partner?.partner?._id ?? partner?.partner
	const partnerName = partner?.partner?.name || 'this partner'

	useEffect(() => {
		let cancelled = false

		const load = async () => {
			setIsLoading(true)
			const action = await dispatch(fetchPortfolioPartnerScope({ portfolioId, partnerUserId }))
			if (cancelled) return

			if (fetchPortfolioPartnerScope.rejected.match(action)) {
				setLoadError('Could not load this portfolio’s locations and projects.')
				setIsLoading(false)
				return
			}

			const loaded: ScopeLocation[] = action.payload?.locations ?? []
			setLocations(loaded)

			// Default every assignable row to Full; the owner narrows from there.
			const defaults: Record<string, AccessChoice> = {}
			loaded.forEach(location => {
				if (!location.alreadyPartner) defaults[`location:${location._id}`] = 'full'
				location.projects.forEach(project => {
					if (!project.alreadyPartner) defaults[`project:${project._id}`] = 'full'
				})
			})
			setChoices(defaults)
			setIsLoading(false)
		}

		load()
		return () => {
			cancelled = true
		}
	}, [dispatch, portfolioId, partnerUserId])

	const assignableKeys = useMemo(() => Object.keys(choices), [choices])

	const setAll = (value: AccessChoice) =>
		setChoices(current =>
			Object.keys(current).reduce(
				(acc, key) => {
					acc[key] = value
					return acc
				},
				{} as Record<string, AccessChoice>,
			),
		)

	const summary = useMemo(() => {
		const counts = { full: 0, readonly: 0, none: 0 }
		assignableKeys.forEach(key => {
			counts[choices[key]] += 1
		})
		const unchanged =
			locations.filter(l => l.alreadyPartner).length +
			locations.reduce((sum, l) => sum + l.projects.filter(p => p.alreadyPartner).length, 0)
		return { ...counts, unchanged }
	}, [assignableKeys, choices, locations])

	const handleConfirm = () => {
		const grants = assignableKeys
			.map(key => {
				const [assetType, assetId] = key.split(':')
				return {
					assetType: assetType as 'location' | 'project',
					assetId,
					accessLevel: choices[key],
				}
			})
			.filter(grant => grant.accessLevel !== 'none')

		onConfirm({ portfolioAccessLevel: futureAccess, grants })
	}

	const renderChoice = (key: string) => (
		<div className="flex items-center gap-1 flex-shrink-0">
			{CHOICES.map(choice => {
				const isSelected = choices[key] === choice.value
				return (
					<button
						key={choice.value}
						type="button"
						onClick={() => setChoices(current => ({ ...current, [key]: choice.value }))}
						className={cn(
							'px-2.5 py-1 text-xs rounded-md border transition-colors',
							isSelected
								? 'border-primary bg-primary/10 text-primary font-medium'
								: 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50',
						)}
					>
						{choice.label}
					</button>
				)
			})}
		</div>
	)

	const unchangedPill = (level: string | null) => (
		<span className="flex items-center gap-1.5 text-xs text-gray-500 flex-shrink-0">
			<Check className="w-3.5 h-3.5 text-success" />
			Already a partner
			{level ? <span className="text-gray-400">({level === 'readonly' ? 'Read Only' : 'Full'})</span> : null}
		</span>
	)

	if (isLoading) {
		return (
			<div className="py-12">
				<Spinner />
			</div>
		)
	}

	if (loadError) {
		return <div className="py-12 text-center text-sm text-destructive">{loadError}</div>
	}

	return (
		<div className="w-full">
			<p className="text-sm text-gray-600 mb-4">
				Choose what {partnerName} can see in each location and project. Anywhere they are
				already a partner is left exactly as it is.
			</p>

			{assignableKeys.length > 0 && (
				<div className="flex items-center gap-2 mb-3 pb-3 border-b border-gray-200">
					<span className="text-xs font-medium text-gray-500">Set all:</span>
					{CHOICES.map(choice => (
						<Button
							key={choice.value}
							variant="primary"
							isOutline
							className="text-xs"
							onClick={() => setAll(choice.value)}
						>
							{choice.label}
						</Button>
					))}
				</div>
			)}

			{locations.length === 0 ? (
				<div className="py-8 text-center text-sm text-gray-500">
					This portfolio has no locations yet. {partnerName} will be added at the portfolio
					level and will inherit the access level below as locations are added.
				</div>
			) : (
				<div className="max-h-[45vh] overflow-y-auto divide-y divide-gray-100">
					{locations.map(location => (
						<div key={location._id} className="py-3">
							<div className="flex items-center gap-3">
								<MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
								<div className="min-w-0 flex-1">
									<div className="text-sm font-medium text-gray-900 truncate">
										{location.assetName}
									</div>
									{(location.city || location.stateProvince) && (
										<div className="text-xs text-gray-500 truncate">
											{[location.city, location.stateProvince].filter(Boolean).join(', ')}
										</div>
									)}
								</div>
								{location.alreadyPartner
									? unchangedPill(location.currentAccessLevel)
									: renderChoice(`location:${location._id}`)}
							</div>

							{location.projects.length > 0 && (
								<div className="mt-2 ml-7 space-y-2">
									{location.projects.map(project => (
										<div key={project._id} className="flex items-center gap-3">
											<Hammer className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
											<span className="text-sm text-gray-700 min-w-0 flex-1 truncate">
												{project.projectName}
											</span>
											{project.alreadyPartner
												? unchangedPill(project.currentAccessLevel)
												: renderChoice(`project:${project._id}`)}
										</div>
									))}
								</div>
							)}
						</div>
					))}
				</div>
			)}

			<div className="mt-4 pt-3 border-t border-gray-200">
				<div className="flex items-center justify-between gap-3">
					<span className="text-sm text-gray-700">
						Locations and projects added to this portfolio later
					</span>
					<div className="flex items-center gap-1 flex-shrink-0">
						{(['full', 'readonly'] as const).map(level => (
							<button
								key={level}
								type="button"
								onClick={() => setFutureAccess(level)}
								className={cn(
									'px-2.5 py-1 text-xs rounded-md border transition-colors',
									futureAccess === level
										? 'border-primary bg-primary/10 text-primary font-medium'
										: 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50',
								)}
							>
								{level === 'full' ? 'Full' : 'Read Only'}
							</button>
						))}
					</div>
				</div>
				<p className="text-xs text-gray-400 mt-1.5">
					Full Access still respects the partner&rsquo;s own account plan &mdash; Community
					accounts never see financials.
				</p>
			</div>

			<div className="flex items-center justify-between gap-3 mt-4 pt-3 border-t border-gray-200">
				<span className="text-xs text-gray-500">
					Full access to {summary.full} &middot; Read only to {summary.readonly} &middot;
					Skipping {summary.none} &middot; {summary.unchanged} unchanged
				</span>
				<div className="flex gap-2">
					<Button variant="primary" isOutline onClick={onCancel}>
						Cancel
					</Button>
					<Button variant="primary" onClick={handleConfirm}>
						Add Partner
					</Button>
				</div>
			</div>
		</div>
	)
}

export default PortfolioPartnerAccessChooser
